import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { DemoService } from 'src/app/services/common/demo.service';
import { environment } from 'src/environments/environment';
import { DemoFlowService } from 'src/app/services/common/demo-flow.service';

/** Una schermata della guida: illustrazione, titolo e testo. */
interface Passo { immagine: string; titolo: string; testo: string; }

/**
 * La guida che accoglie chi atterra nella demo dal telefono: tre schermate che
 * raccontano percorsi, report e avvisi, poi si entra nell'app.
 *
 * È l'equivalente del giro guidato del desktop (app-demo-tour), che sul telefono
 * non funzionerebbe: lì il giro indica i pulsanti della barra in alto, qui lo
 * schermo è troppo stretto per fumetti ancorati agli elementi.
 *
 * Si vede solo entrando da /demo, una volta sola, e mai a chi ha un account:
 * la sessione demo resta accesa anche nelle pagine dopo, ma la guida accoglie e
 * basta, non deve ricomparire a chi sta già girando per l'app.
 */
@Component({
  selector: 'app-demo-benvenuto',
  templateUrl: './demo-benvenuto.component.html',
  styleUrls: ['./demo-benvenuto.component.scss']
})
export class DemoBenvenutoComponent implements OnInit, OnDestroy {

  /** Come DemoTourComponent.giaVisto: chi cambia pagina non se la rivede. */
  private static giaVista = false;

  visibile = false;
  indice = 0;
  private fineDemo: Subscription;

  readonly passi: Passo[] = [
    {
      immagine: 'assets/img/demo/guida-percorsi.webp',
      titolo: 'Ripercorri dove sono stati i tuoi veicoli',
      testo: 'In qualsiasi momento: ogni percorso con tutte le sue soste, giorno per giorno e mezzo per mezzo.'
    },
    {
      immagine: 'assets/img/demo/guida-report.webp',
      titolo: 'Report approfonditi per ogni veicolo',
      testo: 'Chilometri, ore di guida, soste e tempi di lavoro. Pronti quando servono, o nella posta ogni lunedì.'
    },
    {
      immagine: 'assets/img/demo/guida-allarmi.webp',
      titolo: 'Ti avvisiamo noi quando succede qualcosa',
      testo: 'Zone, orari, velocità: imposti la regola una volta e l\'avviso ti arriva sul telefono.'
    }
  ];

  constructor(
    private cd: ChangeDetectorRef,
    private demoFlow: DemoFlowService
  ){}

  ngOnInit(){
    // Se la demo finisce (registrazione dallo stepper e rientro con l'account)
    // la guida sparisce, come la chat e il giro guidato del desktop.
    this.fineDemo = DemoService.chiusa.subscribe(() => this.chiudi());

    // Solo all'ingresso da /demo: environment.originalUrl è l'indirizzo con cui
    // la pagina è stata aperta, quindi un ricaricamento su un'altra pagina non
    // la fa ricomparire.
    const daDemo: boolean = environment.originalUrl?.pathname === '/demo';
    if(!daDemo || !DemoService.active || DemoBenvenutoComponent.giaVista) return;
    DemoBenvenutoComponent.giaVista = true;
    this.visibile = true;
    // I cartellini che spiegano le pagine aspettano che questa si chiuda.
    this.demoFlow.guidaDiBenvenutoAperta = true;
    this.cd.detectChanges();
  }

  ngOnDestroy(){
    this.fineDemo?.unsubscribe();
  }

  get passo(): Passo { return this.passi[this.indice]; }
  get ultimo(): boolean { return this.indice === this.passi.length - 1; }
  get etichettaAvanti(): string { return this.ultimo ? 'Guarda la demo' : 'Avanti'; }

  vaiAl(i: number){
    this.indice = Math.min(Math.max(i, 0), this.passi.length - 1);
    this.cd.detectChanges();
  }

  avanti(){
    if(this.ultimo) return this.chiudi();
    this.vaiAl(this.indice + 1);
  }

  chiudi(){
    if(!this.visibile) return;
    this.visibile = false;
    this.demoFlow.guidaDiBenvenutoAperta = false;
    this.demoFlow.guidaDiBenvenutoChiusa$.next();
    this.cd.detectChanges();
  }

  /** Chi ha già capito e vuole provarlo sui suoi mezzi: si salta la guida. */
  vaiAllaProva(){
    this.chiudi();
    this.demoFlow.vaiAllaRichiestaProva();
  }
}
